import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { watchlistApi } from '../lib/api.js';

// Mirror of usePremiereDeliveryStore but for watchlist items. Kept as a
// separate store so the banner can render premiere and watchlist events
// next to each other without one stomping the other.
export const useWatchlistDeliveryStore = create((set) => ({
  primary: null,
  alerts: [],
  source: 'idle',
  setPayload: (payload, source = 'sse') =>
    set({
      primary: payload?.switch || null,
      alerts: payload?.alerts || [],
      source,
    }),
  dismiss: () => set({ primary: null }),
}));

const POLL_INTERVAL_MS = 15_000;

export default function useWatchlistStream(token) {
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!token) {
      useWatchlistDeliveryStore.setState({ primary: null, alerts: [], source: 'idle' });
      return;
    }

    let source = null;
    let pollTimer = null;
    let cancelled = false;

    const setPayload = (payload, src) => {
      if (cancelled) return;
      useWatchlistDeliveryStore.getState().setPayload(payload, src);
    };

    const startPolling = () => {
      useWatchlistDeliveryStore.setState({ source: 'polling' });
      const tick = async () => {
        try {
          const data = await watchlistApi.pendingSwitch();
          setPayload(data, 'polling');
        } catch { /* swallow */ }
      };
      tick();
      pollTimer = setInterval(tick, POLL_INTERVAL_MS);
    };

    const startSse = () => {
      try {
        source = new EventSource(watchlistApi.eventsUrl(token));
      } catch {
        startPolling();
        return;
      }
      let opened = false;
      source.addEventListener('switch', (e) => setPayload(safeParse(e.data), 'sse'));
      source.addEventListener('alerts', (e) => setPayload(safeParse(e.data), 'sse'));
      source.addEventListener('ping', () => { /* keep-alive */ });
      source.onopen = () => { opened = true; useWatchlistDeliveryStore.setState({ source: 'sse' }); };
      source.onerror = () => {
        if (!opened) {
          source.close();
          source = null;
          startPolling();
        }
      };
    };

    startSse();

    return () => {
      cancelled = true;
      if (source) source.close();
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [token]);
}

function safeParse(raw) {
  try { return JSON.parse(raw); } catch { return null; }
}
